import React, { useState, useRef, useEffect } from "react";
import { FaRupeeSign, FaCopy, FaQrcode, FaDownload, FaSun, FaMoon, FaChartLine } from "react-icons/fa";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";

const UserUpi = () => {
  // Theme state
  const [darkMode, setDarkMode] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    upiId: "",
    amount: "",
    note: "",
    expiryDate: "",
    singleUse: false,
    password: ""
  });

  // QR Customization
  const [qrStyle, setQrStyle] = useState({
    fgColor: "#000000",
    bgColor: "#ffffff",
    logo: "",
    style: "squares" // 'squares', 'dots', 'rounded'
  });

  // Analytics
  const [analytics, setAnalytics] = useState({
    views: 0,
    clicks: 0,
    lastAccessed: null
  });

  const [upiLink, setUpiLink] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [isValidUpi, setIsValidUpi] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const qrCodeRef = useRef(null);

  // Toggle theme
  const toggleTheme = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle QR style changes
  const handleQrStyleChange = (e) => {
    const { name, value } = e.target;
    setQrStyle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setQrStyle(prev => ({
          ...prev,
          logo: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate UPI link
  const generateUPILink = () => {
    if (!formData.upiId.includes("@")) {
      setIsValidUpi(false);
      return;
    }
    
    setIsLoading(true);
    setIsValidUpi(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const { upiId, amount, name, note } = formData;
      const url = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name || "Customer")}&am=${amount || ""}&tn=${encodeURIComponent(note || "Payment")}&cu=INR`;
      
      setUpiLink(url);
      setIsCopied(false);
      setIsLoading(false);
      
      // Track generation event
      setAnalytics(prev => ({
        ...prev,
        views: 0,
        clicks: 0,
        lastAccessed: null
      }));
    }, 800);
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    if (!upiLink) return;
    navigator.clipboard.writeText(upiLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    
    // Track copy event
    setAnalytics(prev => ({
      ...prev,
      clicks: prev.clicks + 1
    }));
  };

  // Download QR Code
  const downloadQRCode = () => {
    if (!qrCodeRef.current) return;
    
    const svg = qrCodeRef.current.querySelector("svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `UPI-Payment-${formData.upiId.split('@')[0]}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  // Open in UPI app
  const openInUpiApp = () => {
    if (!upiLink) return;
    window.location.href = upiLink;
    
    // Track click event
    setAnalytics(prev => ({
      ...prev,
      clicks: prev.clicks + 1,
      lastAccessed: new Date().toISOString()
    }));
  };

  // Theme effect
  useEffect(() => {
    document.documentElement.classList.add(darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`fixed w-full z-50 shadow-lg ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <div className={`${darkMode ? 'bg-blue-600' : 'bg-blue-500'} text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xl`}>
                UPI
              </div>
              <span className="ml-3 text-xl font-bold">MyUPILink</span>
            </Link>
            <div className="flex items-center space-x-4">
              <button 
                onClick={toggleTheme}
                className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 text-yellow-300' : 'bg-gray-200 text-gray-700'}`}
              >
                {darkMode ? <FaSun /> : <FaMoon />}
              </button>
              <nav className="hidden md:flex space-x-6 items-center">
                <a href="#analytics" className="hover:text-blue-400 transition flex items-center">
                  <FaChartLine className="mr-1" /> Analytics
                </a>
                <Link to="/" className={`${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} px-5 py-2 rounded-full text-white transition`}>
                  Generate Link
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Form Section */}
            <div className={`p-6 rounded-xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
              <h1 className="text-3xl font-bold mb-6">Create shareable UPI Payment links!</h1>
              
              <div className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Your Name (Optional)</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. John Doe"
                    className={`w-full rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                  />
                </div>
                
                {/* UPI ID Field */}
                <div>
                  <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>UPI ID or VPA *</label>
                  <input
                    type="text"
                    name="upiId"
                    value={formData.upiId}
                    onChange={handleChange}
                    placeholder="e.g. yourname@upi"
                    className={`w-full rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                    required
                  />
                  {!isValidUpi && (
                    <p className="text-red-400 text-sm mt-1">Please enter a valid UPI ID (must contain @ symbol)</p>
                  )}
                </div>
                
                {/* Amount Field */}
                <div>
                  <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount (Optional)</label>
                  <div className="relative">
                    <span className={`absolute left-3 top-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaRupeeSign />
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="e.g. 1000"
                      className={`w-full rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                    />
                  </div>
                </div>
                
                {/* Note Field */}
                <div>
                  <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Note (Optional)</label>
                  <input
                    type="text"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="e.g. For services rendered"
                    className={`w-full rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                  />
                </div>
                
                {/* Expiry Date */}
                <div>
                  <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Link Expiry Date (Optional)</label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    className={`w-full rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
                  />
                </div>
                
                {/* Security Options */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Security Options</h3>
                  
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="singleUse"
                      name="singleUse"
                      checked={formData.singleUse}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <label htmlFor="singleUse" className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Single-use link (expires after first payment)</label>
                  </div>
                  
                  <div className="mt-3">
                    <label className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Password Protection (Optional)</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Set a password"
                      className={`w-full rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                    />
                  </div>
                </div>
                
                {/* QR Customization */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h3 className={`font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>QR Code Customization</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className={`block mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Foreground Color</label>
                      <div className="flex items-center">
                        <input
                          type="color"
                          name="fgColor"
                          value={qrStyle.fgColor}
                          onChange={handleQrStyleChange}
                          className="w-8 h-8 mr-2"
                        />
                        <span className="text-sm">{qrStyle.fgColor}</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Background Color</label>
                      <div className="flex items-center">
                        <input
                          type="color"
                          name="bgColor"
                          value={qrStyle.bgColor}
                          onChange={handleQrStyleChange}
                          className="w-8 h-8 mr-2"
                        />
                        <span className="text-sm">{qrStyle.bgColor}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className={`block mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>QR Style</label>
                    <select
                      name="style"
                      value={qrStyle.style}
                      onChange={handleQrStyleChange}
                      className={`w-full rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'}`}
                    >
                      <option value="squares">Squares</option>
                      <option value="dots">Dots</option>
                      <option value="rounded">Rounded</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Logo Overlay (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    {qrStyle.logo && (
                      <div className="mt-2 text-sm text-green-500">Logo added successfully!</div>
                    )}
                  </div>
                </div>
                
                {/* Generate Button */}
                <AnimatePresence>
                  <motion.button
                    onClick={generateUPILink}
                    disabled={isLoading}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`w-full ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white font-bold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      "Generate Payment Link"
                    )}
                  </motion.button>
                </AnimatePresence>
                
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Note: If you enter invalid UPI ID, a dummy link will be generated. Please make sure to enter valid UPI ID.
                </p>
              </div>
            </div>

            {/* Result Section */}
            <div className={`p-6 rounded-xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
              <h2 className="text-2xl font-bold mb-6">Your Payment Link</h2>
              
              {upiLink ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  {/* UPI Link Box */}
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>UPI Link:</span>
                      <button
                        onClick={copyToClipboard}
                        className={`flex items-center ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        <FaCopy className="mr-1" />
                        {isCopied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <div className={`p-3 rounded text-sm overflow-x-auto ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-800 border border-gray-200'}`}>
                      {upiLink}
                    </div>
                  </div>
                  
                  {/* QR Code Section */}
                  <div className="flex flex-col items-center">
                    <div 
                      ref={qrCodeRef} 
                      className="p-4 rounded-lg mb-4"
                      style={{ backgroundColor: qrStyle.bgColor }}
                    >
                      <QRCodeSVG
                        value={upiLink}
                        size={200}
                        level="H"
                        includeMargin={true}
                        fgColor={qrStyle.fgColor}
                        bgColor={qrStyle.bgColor}
                        imageSettings={qrStyle.logo ? {
                          src: qrStyle.logo,
                          height: 40,
                          width: 40,
                          excavate: true
                        } : undefined}
                      />
                    </div>
                    
                    <div className="flex flex-wrap justify-center gap-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={downloadQRCode}
                        className={`py-2 px-4 rounded-lg flex items-center ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                      >
                        <FaDownload className="mr-2" /> Download QR
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={openInUpiApp}
                        className={`py-2 px-4 rounded-lg flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                      >
                        <FaQrcode className="mr-2" /> Open in UPI App
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Share Options */}
                  <div className="mt-6">
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Share via:</h3>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { name: "WhatsApp", color: "bg-green-600 hover:bg-green-700" },
                        { name: "Facebook", color: "bg-blue-500 hover:bg-blue-600" },
                        { name: "Email", color: "bg-red-500 hover:bg-red-600" },
                        { name: "SMS", color: "bg-purple-500 hover:bg-purple-600" },
                      ].map((platform, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`text-white py-2 px-4 rounded-lg ${platform.color}`}
                        >
                          {platform.name}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Analytics Section */}
                  <div id="analytics" className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaChartLine className="mr-2" /> Link Analytics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Views</p>
                        <p className="text-2xl font-bold">{analytics.views}</p>
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Clicks</p>
                        <p className="text-2xl font-bold">{analytics.clicks}</p>
                      </div>
                      {analytics.lastAccessed && (
                        <div className="col-span-2">
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Accessed</p>
                          <p className="font-medium">
                            {new Date(analytics.lastAccessed).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-8 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                >
                  <div className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FaQrcode className="text-5xl mx-auto" />
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No UPI Link Generated</h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                    Fill in the details and click "Generate Payment Link" to create your UPI payment link.
                  </p>
                </motion.div>
              )}
            </div>
          </div>

          {/* UPI Apps Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <h3 className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Works with all UPI apps</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                "https://i.ibb.co/KxWgjNr6/amazon.png",
                "https://i.ibb.co/nNcQCXnw/bharatpe.png",
                "https://i.ibb.co/gbNP1F0G/bhim.png",
                "https://i.ibb.co/Wp3rvsLK/Google-Pay-Logo.png",
                "https://i.ibb.co/MyhvH3XC/phonepe.webp"
              ].map((img, index) => (
                <motion.img 
                  key={index}
                  src={img}
                  alt="UPI App"
                  whileHover={{ y: -5 }}
                  className="h-12 object-contain opacity-80 hover:opacity-100 transition"
                />
              ))}
            </div>
          </motion.div>

          {/* How It Works Section */}
          <section id="how-it-works" className="mt-20">
            <h2 className="text-3xl font-bold text-center mb-12">How it works</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Generate Payment Link",
                  description: "Generate payment link by entering your name, VPA address, amount and note.",
                  image: "https://i.ibb.co/QvysGM1m/step1.png"
                },
                {
                  title: "Share Link",
                  description: "Share the payment link with your customers, from whom you want to collect payments.",
                  image: "https://i.ibb.co/xqp6bK7d/step2.png"
                },
                {
                  title: "Get Payment",
                  description: "Customers can click on the link or scan the QR code using any UPI app to complete the payment.",
                  image: "https://i.ibb.co/C5yy0ffL/step3.png"
                }
              ].map((step, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ y: -10 }}
                  className={`p-6 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mb-4 ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} text-white`}>
                    {index + 1}
                  </div>
                  <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>{step.title}</h3>
                  <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{step.description}</p>
                  <img src={step.image} alt={step.title} className="rounded-lg w-full" />
                </motion.div>
              ))}
            </div>
          </section>

          {/* Features Section */}
          <motion.section 
            id="features" 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className={`mt-20 rounded-xl p-8 shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}
          >
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose MyUPILink?</h2>
            <ul className="grid md:grid-cols-2 gap-6">
              {[
                "Create a free UPI payment link to receive payments from your clients.",
                "Collect money with UPI payment apps like Bhim UPI, PhonePe, Google Pay, and others.",
                "100% free, 24 hour, government-approved method of direct payment from sender to recipient.",
                "Eliminating payment gateway taxes can result in annual savings of 2-3% of commission from each sale.",
                "Unrestricted ability to share links on any platform including Instagram, Facebook, WhatsApp, SMS, websites, and more!",
                "Advanced security features including password protection and single-use links."
              ].map((feature, index) => (
                <motion.li 
                  key={index} 
                  whileHover={{ x: 5 }}
                  className="flex items-start"
                >
                  <span className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.section>
        </div>
      </main>

      {/* Footer */}
      <footer className={`py-8 px-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-100 border-t border-gray-200'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <div className={`${darkMode ? 'bg-blue-600' : 'bg-blue-500'} text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xl`}>
                UPI
              </div>
              <span className="ml-3 text-xl font-bold">MyUPILink</span>
            </div>
            <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              © {new Date().getFullYear()} MyUPILink. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserUpi;